// client/src/components/verification/VerifyEmergencyEmailPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Import the NEW specific API function
import { verifyEmergencyEmail } from '../../api/authApi';
import LoadingSpinner from '../common/LoadingSpinner';

import './VerifyPage.css'; // Reuse or create specific styles

const VERIFICATION_TIMEOUT_MS = 20000; // 20 seconds
const REDIRECT_DELAY_MS = 4000; // 4 seconds delay before redirect

function VerifyEmergencyEmailPage() {
    const { t } = useTranslation();
    const { token } = useParams(); // Get token from URL
    const navigate = useNavigate();
    const [verificationStatus, setVerificationStatus] = useState('verifying');
    const [message, setMessage] = useState('');
    const successAchievedRef = useRef(false); // For StrictMode handling

    useEffect(() => {
        let isMounted = true;
        let redirectTimeoutId = null;
        successAchievedRef.current = false; // Reset ref on effect run

        const timeoutId = setTimeout(() => {
            if (isMounted && verificationStatus === 'verifying' && !successAchievedRef.current) {
                console.error("[VerifyEmergencyEmailPage] Verification timed out.");
                setMessage(t('verification.error.timeout', 'Verification took too long. Please try again.'));
                setVerificationStatus('error');
            }
        }, VERIFICATION_TIMEOUT_MS);

        const verifyToken = async () => {
            if (!token) {
                if (isMounted) {
                    setMessage(t('verification.error.missingToken', 'Verification token missing.'));
                    setVerificationStatus('error');
                }
                clearTimeout(timeoutId);
                return;
            }

            if (isMounted && verificationStatus !== 'success' && verificationStatus !== 'error') {
                setVerificationStatus('verifying');
                setMessage(t('verification.verifying', 'Verifying your email...'));
            }

            try {
                console.log("[VerifyEmergencyEmailPage] Calling verifyEmergencyEmail API...");
                // Call the specific API function for emergency emails
                const response = await verifyEmergencyEmail(token);
                console.log("[VerifyEmergencyEmailPage] API call successful:", response);
                if (isMounted) {
                    clearTimeout(timeoutId);
                    setMessage(response.message || t('verification.success.emergencyEmail', 'Emergency email verified successfully!')); // Use specific key
                    setVerificationStatus('success');
                    successAchievedRef.current = true;

                    // Schedule redirect on success
                    console.log(`[VerifyEmergencyEmailPage] Scheduling redirect to /login in ${REDIRECT_DELAY_MS}ms`);
                    redirectTimeoutId = setTimeout(() => {
                        console.log("[VerifyEmergencyEmailPage] Redirecting to /login now.");
                        navigate('/login', { replace: true });
                    }, REDIRECT_DELAY_MS);
                }
            } catch (error) {
                console.error("[VerifyEmergencyEmailPage] Caught error:", error);
                if (isMounted) {
                    clearTimeout(timeoutId);
                    if (!successAchievedRef.current) { // Prevent StrictMode override
                        setMessage(error.message || t('verification.error.generic', 'Verification failed. Please try again or request a new link.'));
                        setVerificationStatus('error');
                    } else { console.log("[VerifyEmergencyEmailPage] StrictMode double-run detected: Ignoring error after success."); }
                }
            }
        };

        verifyToken();

        // Cleanup
        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            if (redirectTimeoutId) { clearTimeout(redirectTimeoutId); }
        };
    }, [token, t, navigate, verificationStatus]); // Added verificationStatus back due to its use in initial loading check logic

    return (

        <div className="verify-page">
            <div className="verify-page-container" style={{ textAlign: 'center', padding: '40px' }}>
                {/* Use specific translation keys */}
                <h1>{t('verification.emergencyEmailTitle', 'Emergency Email Verification')}</h1>
                {verificationStatus === 'verifying' && (
                    <>
                        <LoadingSpinner />
                        {message && <p>{message}</p>}
                    </>
                )}
                {verificationStatus === 'success' && (
                    <div className="verify-success-message">
                        <p>{t('dashboard.account.contact.verification.success', 'Verification successful!')}</p>
                        {/* Keep the link as a fallback */}
                        <p style={{ marginTop: '15px', fontSize: '0.9em' }}>
                            {t('verification.redirecting', 'You will be redirected shortly...')} <Link to="/login">{t('verification.returnToLogin', 'Return to Login')}</Link>
                        </p>
                    </div>
                )}
                {verificationStatus === 'error' && !token && (<div className="error-message">{t('dashboard.account.contact.verification.error', 'Verification failed.')}</div>)}
                
            </div>
        </div >
    );
}

export default VerifyEmergencyEmailPage;